/**
 * Página de listagem de usuários do Admin
 * 
 * Exibe todos os usuários do sistema com paginação server-side,
 * filtro por tenant pesquisável e campo de busca por nome/email.
 * Usa RPC get_admin_users_paginated para evitar limite de 1000 registros.
 */
import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { Users, Check, ChevronsUpDown, Building2, X, Search, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/logger';
import { formatPhoneForDisplay } from '@/lib/phone-utils';

// Interface para role do usuário (retornada pelo RPC como JSONB)
interface UserRole {
  role: string;
  tenant_id: string | null;
}

// Interface para tenant (retornada pelo RPC como JSONB)
interface Tenant {
  id: string;
  name: string;
  slug: string;
}

// Interface completa do usuário com roles e tenants
interface UserWithRoles {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
  phone: string | null;
  roles: UserRole[];
  tenants: Tenant[];
}

const AdminUsers = () => {
  // Estados de dados
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Estados de filtro
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [tenantFilterOpen, setTenantFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalUsers, setTotalUsers] = useState(0);
  
  const { toast } = useToast();

  // Debounce para busca - aguarda 300ms após última digitação
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset para página 1 quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedTenantId]);

  /**
   * Busca usuários via RPC com paginação server-side
   */
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);

      // 1. Buscar lista de tenants para o filtro (apenas uma vez)
      const { data: tenantsData, error: tenantsError } = await supabase
        .from('tenants')
        .select('id, name, slug')
        .order('slug');

      if (tenantsError) throw tenantsError;
      setTenants(tenantsData || []);

      // 2. Buscar usuários paginados via RPC
      const offset = (currentPage - 1) * pageSize;

      const { data, error } = await supabase.rpc('get_admin_users_paginated', {
        p_limit: pageSize,
        p_offset: offset,
        p_search: debouncedSearch || null,
        p_tenant_id: selectedTenantId || null,
      });

      if (error) throw error;

      logger.debug('AdminUsers', `RPC retornou ${data?.length} registros`);

      if (data && data.length > 0) {
        setTotalUsers(Number(data[0].total_count) || 0);

        const mappedUsers: UserWithRoles[] = data.map((row: any) => ({
          id: row.id,
          email: row.email,
          full_name: row.full_name,
          avatar_url: row.avatar_url,
          created_at: row.created_at,
          phone: row.phone || null,
          roles: Array.isArray(row.roles) ? row.roles : [],
          tenants: Array.isArray(row.tenants) ? row.tenants : [],
        }));

        setUsers(mappedUsers);
      } else {
        setUsers([]);
        setTotalUsers(0);
      }
    } catch (error) {
      logger.error('[AdminUsers] Erro ao buscar usuários:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os usuários',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch, selectedTenantId, toast]);

  // Carrega usuários quando filtros/paginação mudam
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /**
   * Retorna as iniciais do nome ou email para o avatar
   */
  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return email.slice(0, 2).toUpperCase();
  };

  /**
   * Retorna a cor do badge baseado no tipo de role
   */
  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'root_admin':
        return 'bg-red-500/10 text-red-600 border-red-500/20';
      case 'tenant_admin':
        return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
      case 'agent':
        return 'bg-green-500/10 text-green-600 border-green-500/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  /**
   * Limpa todos os filtros ativos
   */
  const clearFilters = () => {
    setSelectedTenantId(null);
    setSearchQuery('');
  };

  const hasActiveFilters = selectedTenantId || searchQuery;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Usuários</h1>
        <p className="text-muted-foreground">Gerencie os usuários do sistema</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Lista de Usuários
          </CardTitle>
          <CardDescription>
            {totalUsers > 0
              ? `Exibindo ${users.length} de ${totalUsers} usuários`
              : 'Nenhum usuário encontrado'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Barra de filtros e busca */}
          <div className="flex items-center gap-4 mb-4 flex-wrap">
            {/* Campo de busca */}
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Filtro por tenant */}
            <Popover open={tenantFilterOpen} onOpenChange={setTenantFilterOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={tenantFilterOpen}
                  className="w-[280px] justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    {selectedTenantId
                      ? tenants.find((t) => t.id === selectedTenantId)?.slug
                      : "Filtrar por tenant..."}
                  </div>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0">
                <Command>
                  <CommandInput placeholder="Pesquisar tenant..." />
                  <CommandList>
                    <CommandEmpty>Nenhum tenant encontrado.</CommandEmpty>
                    <CommandGroup>
                      {tenants.map((tenant) => (
                        <CommandItem
                          key={tenant.id}
                          value={tenant.slug}
                          onSelect={() => {
                            setSelectedTenantId(tenant.id);
                            setTenantFilterOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              selectedTenantId === tenant.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {tenant.slug}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {/* Botão para limpar filtros */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Limpar filtros
              </Button>
            )}
          </div>

          {/* Tabela de usuários */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Carregando...</span>
            </div>
          ) : users.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              {hasActiveFilters
                ? 'Nenhum usuário encontrado com os filtros aplicados'
                : 'Nenhum usuário cadastrado'}
            </p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Telefone</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Criado em</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatar_url || undefined} />
                            <AvatarFallback>
                              {getInitials(user.full_name, user.email)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium">
                            {user.full_name || 'Sem nome'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {user.email}
                      </TableCell>
                      {/* Coluna de telefone formatado */}
                      <TableCell className="text-muted-foreground">
                        {user.phone ? formatPhoneForDisplay(user.phone) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.tenants.length === 0 ? (
                            <span className="text-muted-foreground text-sm">Sem tenant</span>
                          ) : (
                            user.tenants.map((tenant) => (
                              <Badge
                                key={tenant.id}
                                variant="outline"
                                className="bg-purple-500/10 text-purple-600 border-purple-500/20"
                              >
                                {tenant.slug}
                              </Badge>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {user.roles.length === 0 ? (
                            <Badge variant="outline">Sem role</Badge>
                          ) : (
                            user.roles.map((role, idx) => (
                              <Badge
                                key={idx}
                                variant="outline"
                                className={getRoleBadgeColor(role.role)}
                              >
                                {role.role}
                              </Badge>
                            ))
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(user.created_at), 'dd/MM/yyyy HH:mm')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Paginação */}
              <PaginationFooter
                totalItems={totalUsers}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsers;
